import { useContext } from 'react';
import { FC, ReactNode } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import UserContext from 'src/contexts/UserContext';
import { emailVerifiedRedirect } from './emailVerifiedRedirect';
import {
  clearPostLoginRedirect,
  setPostLoginRedirect,
} from './postLoginRedirect';

interface AuthGuardProps {
  children?: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { userData, isServerDown } = useContext(UserContext);
  const location = useLocation();

  const userContext = userData || {};

  if (!isAuthenticated) {
    // Stash where the user was headed (path + query) so login can return them
    // here — the same mechanism SignedInGuard uses for the MCP consent page.
    // This guard fronts the whole /app tree, and the emails deep-link into it:
    // the bank reconnect button is /app/account/bank?relink=<token>. Without
    // the stash, a signed-out click lost the token at this redirect and landed
    // on the dashboard, and the one-click reconnect became a scavenger hunt.
    //
    // Stored in BOTH places on purpose (see postLoginRedirect.ts): history
    // state is the fast path, but a full page reload — which an email click
    // in a fresh browser always is — and the Firebase auth re-init both wipe
    // it. sessionStorage survives both; GuestGuard consumes whichever exists.
    const from = location.pathname + location.search;
    setPostLoginRedirect(from);
    return <Redirect to={{ pathname: '/login', state: { from } }} />;
  }

  const verifyRedirect = emailVerifiedRedirect(user);
  if (verifyRedirect) {
    // Re-stash across the verify-email detour, so the deep link survives it
    // instead of dead-ending on the dashboard afterwards.
    setPostLoginRedirect(location.pathname + location.search);
    return verifyRedirect;
  }

  if (
    userContext.signupCompleted === true &&
    userContext.subscriptionCanceled !== null &&
    (new Date(userContext.subscriptionEndDate?._seconds * 1000) < new Date() ||
      userContext.subscriptionEndDate === undefined)
  ) {
    return <Redirect to="/app/pricing" />;
  }

  if (userContext.signupCompleted === false) {
    return <Redirect to="/app/account/form" />;
  }

  if (isServerDown) {
    return <Redirect to="/upgrading" />;
  }

  // The user has ARRIVED on the guarded destination — consume the stash
  // (idempotent; only clears when it matches this exact path + query).
  // Deliberately below the pricing/incomplete-signup/upgrading redirects:
  // those detours are not the destination, and arrival-based consumption
  // means none of them can burn the deep link before it was honored.
  clearPostLoginRedirect(location.pathname + location.search);

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
